import type Feature from "ol/Feature";
import type OlMap from "ol/Map";
import { unByKey } from "ol/Observable";
import type { EventsKey } from "ol/events";
import type Geometry from "ol/geom/Geometry";
import Draw from "ol/interaction/Draw";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ToggleControl, useNgwMapContext } from "@nextgis/react-ngw-map";
import type { ControlOptions, ToggleControlOptions } from "@nextgis/webmap";

import { useTranslation } from "../../../i18n";
import {
  createMeasureStyle,
  formatMeasureGeometry,
  getMeasureTooltipCoordinate,
} from "../../../utils/measure";

import { MeasureTooltip } from "./MeasureTooltip";

import "./ToolMeasure.css";

type MeasureKind = "LineString" | "Polygon";

type TooltipState = {
  id: string;
  position: number[];
  value: string;
};

type ToolMeasureProps = Omit<
  ToggleControlOptions,
  "html" | "onClick" | "title"
> &
  ControlOptions & {
    type: MeasureKind;
  };

function MeasureIcon({ type }: { type: MeasureKind }) {
  const iconClass =
    type === "LineString"
      ? "tool-svg-measure-distance"
      : "tool-svg-measure-area";

  return (
    <span
      className={`tool-svg tool-svg-mask ${iconClass}`}
      aria-hidden="true"
    />
  );
}

export function ToolMeasure({ type, ...controlProps }: ToolMeasureProps) {
  const { locale, t } = useTranslation();
  const { ngwMap } = useNgwMapContext();
  const olMap = ngwMap.mapAdapter?.map as OlMap | undefined;

  const sourceRef = useRef(new VectorSource<Feature<Geometry>>());
  const drawRef = useRef<Draw | null>(null);
  const changeListenerRef = useRef<EventsKey | null>(null);
  const idRef = useRef(0);

  const [currentTooltipId, setCurrentTooltipId] = useState<string | null>(null);
  const [tooltips, setTooltips] = useState<Map<string, TooltipState>>(
    new Map(),
  );
  const [toRemoveIds, setToRemoveIds] = useState<string[]>([]);

  const title = useMemo(
    () => (type === "LineString" ? t("measure.distance") : t("measure.area")),
    [type, t],
  );

  const unbindChangeListener = useCallback(() => {
    if (changeListenerRef.current) {
      unByKey(changeListenerRef.current);
      changeListenerRef.current = null;
    }
  }, []);

  const clearMeasure = useCallback(() => {
    unbindChangeListener();
    sourceRef.current.clear();
    setTooltips(new Map());
    setCurrentTooltipId(null);
  }, [unbindChangeListener]);

  const updateTooltip = useCallback(
    (id: string, geometry: Geometry): string | null => {
      const position = getMeasureTooltipCoordinate(geometry);
      const value = formatMeasureGeometry(geometry, locale);

      if (!position || !value) return null;

      setTooltips((current) => {
        const prev = current.get(id);
        if (
          prev &&
          prev.value === value &&
          prev.position[0] === position[0] &&
          prev.position[1] === position[1]
        ) {
          return current;
        }

        const next = new Map(current);
        next.set(id, { id, position, value });
        return next;
      });

      return value;
    },
    [locale],
  );

  const closeTooltip = useCallback((id: string) => {
    setTooltips((current) => {
      if (!current.has(id)) return current;
      const next = new Map(current);
      next.delete(id);
      return next;
    });
    setToRemoveIds((prev) => [...prev, id]);
  }, []);

  useEffect(() => {
    if (toRemoveIds.length === 0) return;

    toRemoveIds.forEach((id) => {
      const feature = sourceRef.current.getFeatureById(id);
      if (feature) {
        sourceRef.current.removeFeature(feature);
      }
    });

    setToRemoveIds([]);
  }, [toRemoveIds]);

  useEffect(() => {
    if (!olMap) return;

    const style = createMeasureStyle();
    const layer = new VectorLayer({
      source: sourceRef.current,
      style,
      zIndex: 999,
    });
    const draw = new Draw({
      source: sourceRef.current,
      type,
      style,
    });

    draw.setActive(false);
    drawRef.current = draw;
    olMap.addLayer(layer);
    olMap.addInteraction(draw);

    draw.on("drawstart", (event) => {
      const id = `measure-${++idRef.current}`;
      event.feature.setId(id);
      setCurrentTooltipId(id);

      const geometry = event.feature.getGeometry();
      if (!geometry) return;

      updateTooltip(id, geometry);
      changeListenerRef.current = geometry.on("change", (changeEvent) => {
        updateTooltip(id, changeEvent.target as Geometry);
      });
    });

    draw.on("drawend", (event) => {
      const id = event.feature.getId();
      const geometry = event.feature.getGeometry();

      unbindChangeListener();
      setCurrentTooltipId(null);

      if (id !== undefined && geometry) {
        updateTooltip(String(id), geometry);
      }
    });

    return () => {
      draw.setActive(false);
      olMap.removeInteraction(draw);
      olMap.removeLayer(layer);
      clearMeasure();
      drawRef.current = null;
    };
  }, [clearMeasure, olMap, type, unbindChangeListener, updateTooltip]);

  const setActive = useCallback(
    (active: boolean) => {
      const draw = drawRef.current;
      if (!draw) return;

      draw.setActive(active);
      if (!active) {
        clearMeasure();
      }
    },
    [clearMeasure],
  );

  return (
    <>
      <ToggleControl {...controlProps} title={title} onClick={setActive}>
        <MeasureIcon type={type} />
      </ToggleControl>

      {olMap &&
        Array.from(tooltips.values()).map((tooltip) => (
          <MeasureTooltip
            key={tooltip.id}
            map={olMap}
            position={tooltip.position}
            staticMode={currentTooltipId !== tooltip.id}
            onClose={() => closeTooltip(tooltip.id)}
          >
            {tooltip.value}
          </MeasureTooltip>
        ))}
    </>
  );
}
